import { SettingsList } from './SettingsList';
import type { AgentCapability, AgentCredentialView } from '@api/automation/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { agentCredentialOptions, ApiError, getAgentCredentials, issueAgentCredential, isStatus, revokeAgentCredential } from '../lib/api';
import { signWithKey } from '../lib/webauthn';
import { ago, absolute } from '../lib/time';
import { useScope } from '../scope/scope';
import { AgentBatches } from './AgentBatches';
import { AgentWindows } from './AgentWindows';
import { AgentElevations } from './AgentElevations';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/misc';
import { useToast } from '../ui/toast';

const CAPABILITIES: Array<[AgentCapability, string, string]> = [
  ['customers.read', 'List customers, sites and devices', 'So it can show you the choices'],
  ['devices.enroll', 'Make installers', 'For the customer and site you choose'],
  ['devices.investigate', 'Read device software and activity', 'Installed versions, install dates and recent events. Read-only; it changes nothing'],
  ['devices.rename', 'Set friendly device names', 'Set or clear names on any device in this workspace; shown in the console, customer portal and tray. No computer settings or remote access'],
  ['devices.telemetry.read', 'Read device facts and connection state', 'Hardware and serial numbers, network addresses, signed-in users and presence, drives, antivirus, Falcon installation state and site links, updates, restart status, performance and findings. Read-only'],
  ['devices.jobs.read', 'Read all device job output', 'Status, exit codes and stored stdout/stderr for permitted devices, including jobs requested by people and other agents. Read-only'],
  ['devices.run', 'Ask to run scripts and runbooks on devices', 'As SYSTEM or root, on the devices you name. Nothing that changes a device runs until you approve that batch with your key'],
  ['customers.create', 'Add customers and set up CrowdStrike', 'Only ones you name. CrowdStrike creates a Falcon child, billed only once sensors are installed; sites are linked to their Falcon group. Installing Falcon still takes your key'],
];
const DAYS: Array<[number, string]> = [[30, '30 days'], [90, '90 days'], [365, '1 year']];
const field = 'h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50';

/**
 * Agent access (WRMM-163): an MSP-wide owner gives an AI agent or script its own credential for a few actions (listing
 * customers, sites and devices, making installers, reading a device's software and activity, WRMM-279, and adding customers, WRMM-325). It works within the owner's own access, can
 * only do what is ticked here, and is in the audit trail as the agent. The token is shown once.
 */
export function AgentAccess() {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const key = ['scope', mspId, 'agent-credentials'];
  const list = useQuery({ queryKey: key, queryFn: ({ signal }) => getAgentCredentials(mspId, signal), retry: false });
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<AgentCapability[]>(['customers.read', 'devices.enroll']);
  const [days, setDays] = useState(90);
  const [shown, setShown] = useState<{ name: string; token: string } | null>(null);
  const issue = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), capabilities: picked, expiresInDays: days };
      return issueAgentCredential(mspId, body, await signWithKey(await agentCredentialOptions(mspId, body)));
    },
    onSuccess: issued => { void client.invalidateQueries({ queryKey: key }); setShown({ name: issued.credential.name, token: issued.token }); setName(''); },
    onError: error => toast({ tone: 'warning', title: 'The credential wasn’t issued', body: isStatus(error, 409)
      ? 'Register your security key or passkey first (Settings, Security keys): issuing takes a key touch.'
      : 'Touch your key when asked. It needs a name and at least one permission, and only an MSP-wide owner can issue one.' }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeAgentCredential(mspId, id),
    onSuccess: views => { client.setQueryData<AgentCredentialView[]>(key, views); toast({ title: 'Credential revoked', body: 'Its next request is refused, and installers it made stop working.' }); },
  });
  if (list.isPending) return <Skeleton className="h-24 w-full" />;
  if (list.isError) {
    return <p className="text-[13px] text-ink-3">{list.error instanceof ApiError && list.error.status === 403
      ? 'Only an MSP-wide owner can give agents access.' : 'Agent access couldn’t be loaded.'}</p>;
  }
  const toggle = (capability: AgentCapability) => setPicked(p => (p.includes(capability) ? p.filter(c => c !== capability) : [...p, capability]));
  return (
    <div className="space-y-4">
      <AgentWindows />
      <AgentBatches />
      <AgentElevations />
      {shown && (
        <div role="status" className="space-y-2 rounded-lg border border-attention/40 bg-attention/5 p-3 text-[13px]">
          <p className="font-medium">The credential for {shown.name}. Copy it now: it is shown only once.</p>
          <code className="block break-all rounded bg-panel px-2 py-1.5 font-mono text-[12.5px]">{shown.token}</code>
          <p className="text-ink-2">Store it where only that agent can read it. Anyone with it can do what you ticked, as that agent, until it expires or you revoke it.</p>
          <Button size="sm" variant="secondary" onClick={() => { void navigator.clipboard?.writeText(shown.token); }}>Copy</Button>
          <Button size="sm" variant="secondary" onClick={() => setShown(null)}>Done</Button>
        </div>
      )}
      <form className="space-y-3" onSubmit={e => { e.preventDefault(); issue.mutate(); }}>
        <label className="block text-[13px] text-ink-2">Agent name
          <input value={name} onChange={e => setName(e.target.value)} maxLength={80} placeholder="Claude on Dora44" className={`${field} mt-1`} /></label>
        <fieldset className="space-y-1.5">
          <legend className="text-[13px] text-ink-2">It may</legend>
          {CAPABILITIES.map(([capability, label, hint]) => (
            <label key={capability} className="flex items-start gap-2 text-[13.5px]">
              <input type="checkbox" className="mt-1" checked={picked.includes(capability)} onChange={() => toggle(capability)} />
              <span>{label} <span className="block text-[12.5px] text-ink-3">{hint}</span></span>
            </label>
          ))}
        </fieldset>
        <label className="flex items-center gap-2 text-[13px] text-ink-2">Expires after
          <select aria-label="Expires after" value={days} onChange={e => setDays(Number(e.target.value))}
            className="h-8 rounded-lg border border-line bg-panel px-2 text-[13px] outline-none">{DAYS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}</select></label>
        <Button type="submit" disabled={issue.isPending || !name.trim() || !picked.length}>{issue.isPending ? 'Touch your key…' : 'Issue credential'}</Button>
        <p className="text-[12.5px] text-ink-3">Issuing takes a touch of your security key or passkey.</p>
      </form>
      {list.data.length > 0 && (
        <SettingsList id="agent-credentials" title="Agent credentials" items={list.data} newest={c => c.createdAt}>{visible => <ul className="divide-y divide-line rounded-lg border border-line" aria-label="Agent credentials">
          {visible.map(c => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[13px]">
              <span className="min-w-0 flex-1"><span className="font-medium">{c.name}</span>
                <span className="block text-[12.5px] text-ink-3">{c.capabilities.join(', ')} · {c.revokedAt ? `revoked ${ago(c.revokedAt)}`
                  : `expires ${absolute(c.expiresAt)}`} · {c.lastUsedAt ? `last used ${ago(c.lastUsedAt)}` : 'never used'}</span></span>
              {!c.revokedAt && <Button size="sm" variant="secondary" disabled={revoke.isPending} onClick={() => revoke.mutate(c.id)}>Revoke</Button>}
            </li>
          ))}
        </ul>}</SettingsList>
      )}
    </div>
  );
}
